// dashboard/src/components/credits/DualCreditBalanceCard.tsx
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCreditBalance } from '../../services/creditService';
import { formatDate } from '../../utils/dateUtils';

const DualCreditBalanceCard: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['creditBalance'],
    queryFn: getCreditBalance,
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-12 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6 border-l-4 border-error-500">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Credit Balance</h3>
        <p className="text-error-500">Error loading credit balance. Please try again later.</p>
      </div>
    );
  }

  const planCreditsRemaining = data?.plan_credits?.remaining || 0;
  const customCreditsAvailable = data?.custom_credits?.available || 0;
  const totalCredits = data?.total_available_credits || 0;
  const currentPlan = data?.plan_credits?.current_plan || 'free';
  const planPeriodEnd = data?.plan_credits?.plan_period_end;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Credit Balance</h3>
      
      {/* Total Credits */}
      <div className="flex items-baseline mb-6">
        <span className="text-3xl font-bold chart-primary">{totalCredits.toLocaleString()}</span>
        <span className="ml-2 text-sm text-gray-500">total credits</span>
      </div>

      {/* Credit Breakdown */}
      <div className="space-y-4">
        {/* Plan Credits */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-blue-900">Plan Credits</h4>
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
              {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
            </span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-lg font-semibold text-blue-900">
              {planCreditsRemaining.toLocaleString()}
            </span>
            <span className="text-xs text-blue-600">
              {planPeriodEnd ? `Expires ${formatDate(planPeriodEnd)}` : 'No active plan'}
            </span>
          </div>
          {(data?.plan_credits?.total_allocated || 0) > 0 && (
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{
                  width: `${Math.max(0, (planCreditsRemaining / (data?.plan_credits?.total_allocated || 1)) * 100)}%`
                }}
              ></div>
            </div>
          )}
        </div>

        {/* Custom Credits */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-green-900">Custom Credits</h4>
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
              Never Expire
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-green-900">
              {customCreditsAvailable.toLocaleString()}
            </span>
            <span className="text-xs text-green-600">
              Purchased separately
            </span>
          </div>
        </div>
      </div>

      {/* Usage Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm text-gray-500 mb-1">
          <span>Last 30 Days Usage</span>
          <span>{data?.usage_summary?.last_30_days_usage?.toLocaleString() || '0'} credits</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="progress-primary h-2 rounded-full"
            style={{
              width: `${Math.min(100, ((data?.usage_summary?.last_30_days_usage || 0) / Math.max(1, totalCredits || 1)) * 100)}%`
            }}
          ></div>
        </div>
      </div>

      {/* Account Info */}
      <div className="mt-4 text-xs text-gray-500">
        <p>Account created: {formatDate(data?.created_at || '')}</p>
        <p>Last updated: {formatDate(data?.updated_at || '')}</p>
      </div>
    </div>
  );
};

export default DualCreditBalanceCard;
